// $FlowFixMe
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { injectIntl, FormattedDate } from 'react-intl';
import { bindActionCreators } from 'redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { pathOr } from 'ramda';
import {
  Box,
  Stack,
  Button,
  Spinner,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

import confirm from '../../../../../../components/ConfirmDialogModal';
import ElementWithPermissions from '../../../../../../components/ElementWithPermissions';
import ConditionalTooltip from '../../../../../../components/ConditionalTooltip';

import { editLease, sendToPortal } from '../../../../actions';
import {
  PORTAL_ACTION_SENT,
  PORTAL_ACTION_EXECUTED,
  PORTAL_SIGNATURE_STATUS_SIGNED,
} from '../../../../../../utils/lease-helpers';

import ExecuteSignatureModal from '../ButtonActionModals/ExecuteSignatureModal';
import messages from './messages';
import { useGateTooltip } from './hooks';

type ElectronicMethodButtonsProps = {
  allAffordableTabsApproved: boolean,
  applicationId: string,
  canComplete: boolean,
  difference: number,
  hasNonFR: boolean,
  isResident: boolean,
  leaseId: string,
  leaseSignatureStatuses: Object,
  onGenerate: Function,
  profileId: string,
  prospectId: string,
  transferCertBlocked?: boolean,
  transferCertTooltip?: string,
  unapprovedAffordablePrograms?: Array<string>,
  moveInCertBlocked?: boolean,
  moveInCertTooltip?: string,
  providerUnknown?: boolean,
  isEditLeaseMode?: boolean,
  setIsEditLeaseMode?: Function,
};

type ElectronicMethodButtonsInjectedProps = {
  actions: Object,
  intl: Object,
};

export const ElectronicMethodButtons = ({
  actions,
  allAffordableTabsApproved,
  applicationId,
  canComplete,
  difference,
  hasNonFR,
  intl,
  isResident,
  leaseId,
  leaseSignatureStatuses,
  onGenerate,
  profileId,
  prospectId,
  transferCertBlocked = false,
  transferCertTooltip = '',
  unapprovedAffordablePrograms = [],
  moveInCertBlocked = false,
  moveInCertTooltip = '',
  providerUnknown = false,
  isEditLeaseMode = false,
  setIsEditLeaseMode,
}: ElectronicMethodButtonsProps & ElectronicMethodButtonsInjectedProps) => {
  const [showExecuteModal, setShowExecuteModal] = useState(false);

  const handleShowExecuteClick = () => setShowExecuteModal(true);
  const handleHideExecuteClick = () => setShowExecuteModal(false);
  // The dialog has its own submit, so an unknown provider closes it as well as disabling the button.
  useEffect(() => {
    if (providerUnknown) setShowExecuteModal(false);
  }, [providerUnknown]);

  const { latestLog, signatures } = leaseSignatureStatuses;

  const confirmEditDialog = () => {
    // With the provider unknown the lease may be a Blue Moon lease. The signature statuses are not
    // keyed by lease, so they cannot rule Portal out either: the warning says "may".
    const editWarning = providerUnknown
      ? messages.confirmEditLeaseProviderUnknown
      : messages.confirmEditElectronicLease;
    confirm(intl.formatMessage(editWarning)).then(() => {
      actions.editLease(
        leaseId,
        profileId,
        isResident,
        prospectId,
        undefined,
        undefined,
        setIsEditLeaseMode,
      );
    });
  };

  const handleSendToPortalClick = () =>
    actions.sendToPortal(leaseId, profileId, isResident, prospectId);

  const portalAction = pathOr(null, ['action'], latestLog);
  const portalActionDate = pathOr(null, ['createdAt'], latestLog);
  const notSignedLeases = signatures.filter(
    (sig) => sig.signatureStatus !== PORTAL_SIGNATURE_STATUS_SIGNED,
  );

  const leaseSent = portalAction === PORTAL_ACTION_SENT;
  const leaseExecuted = portalAction === PORTAL_ACTION_EXECUTED;

  const { automateMonthlyLeaseTransactionsMvp: automateMLTsFlag } = useFlags();
  const notReconciled = automateMLTsFlag ? false : difference !== 0;

  const { gateTooltip, showGateTooltip } = useGateTooltip({
    intl,
    allAffordableTabsApproved,
    unapprovedAffordablePrograms,
    transferCertBlocked,
    transferCertTooltip,
    moveInCertBlocked,
    moveInCertTooltip,
    providerUnknown,
  });

  return (
    <>
      <ExecuteSignatureModal
        applicationId={applicationId}
        isResident={isResident}
        leaseId={leaseId}
        onHide={handleHideExecuteClick}
        profileId={profileId}
        prospectId={prospectId}
        show={showExecuteModal}
      />
      <ElementWithPermissions scope={['lease-create']}>
        <Stack spacing={1} direction={'row'} justifyContent={'center'}>
          {/* The outer wrapper disables only the Stack, so Edit needs its own. It takes the scopes the
              cancel route accepts. */}
          <ElementWithPermissions scope={['lease-create', 'lease-cancel']}>
            <Button
              sx={{ flex: 1, maxWidth: '150px' }}
              data-testid={'editLeaseButton'}
              variant={'primarySubtle'}
              onClick={confirmEditDialog}
              disabled={isEditLeaseMode}
            >
              {isEditLeaseMode ? (
                <Spinner small />
              ) : (
                intl.formatMessage(messages.edit)
              )}
            </Button>
          </ElementWithPermissions>
          <>
            {!isEditLeaseMode && (
              <>
                {!leaseSent && !leaseExecuted && (
                  <ConditionalTooltip
                    containerSX={{
                      display: 'flex',
                      flex: 1,
                      maxWidth: '150px',
                    }}
                    show={showGateTooltip}
                    title={gateTooltip}
                    placement={'top'}
                    variant={'light'}
                  >
                    {/* span wrapper: a disabled MUI button swallows pointer events. Carries the
                        flex sizing itself since it — not the Button — is now the Stack's flex item.
                        ElementWithPermissions sits inside so the Button is its direct cloned child:
                        the outer lease-create wrapper's only direct child is the <Stack>, so the
                        Stack absorbs that clone and the buttons never see it. */}
                    <ElementWithPermissions scope={['lease-create']}>
                      <Button
                        sx={{ flex: 1, maxWidth: '150px' }}
                        data-testid={'sendToPortalButton'}
                        variant={'shout'}
                        onClick={handleSendToPortalClick}
                        disabled={
                          !canComplete ||
                          notReconciled ||
                          hasNonFR ||
                          !allAffordableTabsApproved ||
                          moveInCertBlocked ||
                          transferCertBlocked ||
                          providerUnknown
                        }
                      >
                        {intl.formatMessage(messages.sendToPortal)}
                      </Button>
                    </ElementWithPermissions>
                  </ConditionalTooltip>
                )}
                {leaseSent && !leaseExecuted && (
                  <ConditionalTooltip
                    containerSX={{
                      display: 'flex',
                      flex: 1,
                      maxWidth: '150px',
                    }}
                    show={showGateTooltip}
                    title={gateTooltip}
                    placement={'top'}
                    variant={'light'}
                  >
                    {/* span wrapper: a disabled MUI button swallows pointer events.
                        ElementWithPermissions stays inside the span so the Button remains its
                        direct cloned child and still receives the permission-driven `disabled`.
                        Carries the flex sizing itself since it — not the Button — is now the
                        Stack's flex item. */}
                    <ElementWithPermissions scope={['lease-execute']}>
                      <Button
                        sx={{ flex: 1, maxWidth: '150px' }}
                        variant={'shout'}
                        data-testid={'executeLeaseButton'}
                        onClick={handleShowExecuteClick}
                        disabled={
                          notSignedLeases.length > 0 ||
                          hasNonFR ||
                          !allAffordableTabsApproved ||
                          moveInCertBlocked ||
                          transferCertBlocked ||
                          providerUnknown
                        }
                      >
                        {intl.formatMessage(messages.executeLease)}
                      </Button>
                    </ElementWithPermissions>
                  </ConditionalTooltip>
                )}
                <ConditionalTooltip
                  containerSX={{ display: 'flex', flex: 1, maxWidth: '150px' }}
                  show={showGateTooltip}
                  title={gateTooltip}
                  placement={'top'}
                  variant={'light'}
                >
                  {/* span wrapper: a disabled MUI button swallows pointer events. Carries the
                      flex sizing itself since it — not the Button — is now the Stack's flex item.
                      ElementWithPermissions sits inside for the same reason as Send to Portal. */}
                  <ElementWithPermissions scope={['lease-create']}>
                    <Button
                      sx={{ flex: 1, maxWidth: '150px' }}
                      data-testid={'downloadLeaseButton'}
                      variant={'primary'}
                      disabled={
                        !canComplete ||
                        notReconciled ||
                        hasNonFR ||
                        !allAffordableTabsApproved ||
                        moveInCertBlocked ||
                        transferCertBlocked ||
                        providerUnknown
                      }
                      onClick={onGenerate}
                    >
                      {intl.formatMessage(messages.downloadLease)}
                    </Button>
                  </ElementWithPermissions>
                </ConditionalTooltip>
              </>
            )}
          </>
        </Stack>
        <Box marginTop={1}>
          {/* The signature statuses are not keyed by lease, so with the provider unknown a sent
              status may belong to the lease shown before. */}
          {leaseSent &&
            !leaseExecuted &&
            portalActionDate &&
            !isEditLeaseMode &&
            !providerUnknown && (
              <div className="text-center" style={{ marginRight: '3%' }}>
                {`${intl.formatMessage(messages.sentToPortal)} - `}
                <FormattedDate value={portalActionDate} />
              </div>
            )}
          {notReconciled && (
            <div className="text-center" style={{ marginRight: '3%' }}>
              {intl.formatMessage(messages.notReconciled)}
            </div>
          )}
        </Box>
      </ElementWithPermissions>
    </>
  );
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators({ editLease, sendToPortal }, dispatch);
  return { actions };
}

const InjectedElectronicMethodButtons = injectIntl(ElectronicMethodButtons);

export default connect(
  null,
  mapDispatchToProps,
)(InjectedElectronicMethodButtons);
